import { FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock, CheckCircle2 } from 'lucide-react';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { PasswordField, FormError, SubmitButton } from '@/components/auth/FormFields';
import { resetPassword, readApiError } from '@/lib/auth-api';

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<{ password?: string; confirm?: string }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <AuthLayout title={t('auth.reset.invalidTitle')} subtitle={t('auth.reset.invalidBody')}>
        <Link to="/forgot-password" className="gradient-primary inline-flex w-full h-11 items-center justify-center rounded-xl text-white text-sm font-semibold shadow-glow">
          {t('auth.reset.requestNew')}
        </Link>
      </AuthLayout>
    );
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const next: typeof errors = {};
    if (password.length < 8) next.password = t('auth.errors.passwordMin');
    if (password !== confirm) next.confirm = t('auth.errors.passwordMismatch');
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setLoading(true);
    setFormError(null);
    try {
      await resetPassword(token, password);
      setDone(true);
      setTimeout(() => nav('/login', { replace: true }), 1500);
    } catch (err) {
      setFormError(readApiError(err, t('auth.errors.resetFailed')));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <AuthLayout title={t('auth.reset.doneTitle')} subtitle={t('auth.reset.doneBody')}>
        <div className="rounded-2xl border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50 dark:bg-emerald-500/10 p-4 flex items-start gap-3">
          <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
          <p className="text-sm text-emerald-700 dark:text-emerald-300">{t('auth.reset.doneNote')}</p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('auth.reset.title')} subtitle={t('auth.reset.subtitle')}>
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <FormError message={formError} />
        <PasswordField
          label={t('auth.reset.newPassword')}
          autoComplete="new-password"
          placeholder={t('auth.placeholders.password')}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={errors.password}
          leadingIcon={<Lock className="h-4 w-4" />}
          showStrength
          required
        />
        <PasswordField
          label={t('auth.reset.confirmPassword')}
          autoComplete="new-password"
          placeholder={t('auth.placeholders.password')}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          error={errors.confirm}
          leadingIcon={<Lock className="h-4 w-4" />}
          required
        />
        <SubmitButton loading={loading}>{t('auth.reset.submit')}</SubmitButton>
      </form>
    </AuthLayout>
  );
}
